import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Upload, FileText, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
const LOBS = ['Homeowners','Auto','Commercial','Life','Health','Umbrella','Flood','Renters','Dwelling','Manufactured','Condo','Workers Comp','General Liability','Inland Marine','Other'];
export { STATES, LOBS };

export const UploadModal = ({ headers, users, isAdmin, onClose, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [carrier, setCarrier] = useState('');
  const [state, setState] = useState('');
  const [lob, setLob] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [reminderFreq, setReminderFreq] = useState('90');
  const [escalationAfter, setEscalationAfter] = useState('3');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleUpload = async () => {
    if (!file) return toast.error('Select a file');
    if (!carrier.trim()) return toast.error('Carrier name required');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('carrier', carrier);
      fd.append('state', state);
      fd.append('lob', lob);
      fd.append('assigned_to', assignedTo);
      fd.append('reminder_frequency', reminderFreq);
      fd.append('escalation_after', escalationAfter);
      fd.append('notes', notes);

      const res = await fetch(`${API}/api/appetite/documents`, {
        method: 'POST', headers: { 'Authorization': headers()['Authorization'] }, body: fd,
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Upload failed'); return; }
      toast.success('Document uploaded — indexing in background. It will be searchable shortly.');
      onUploaded();
    } catch (err) {
      toast.error(`Upload failed: ${err?.message || 'network error'}`);
    }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-8 px-4 overflow-y-auto" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mb-8" data-testid="upload-modal">
        <div className="px-5 py-3 bg-[#101C47] rounded-t-xl flex items-center justify-between">
          <span className="font-bold text-sm text-white">Upload Underwriting Guideline</span>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {/* File */}
          <div>
            <label className="text-[10px] font-semibold text-gray-500 block mb-1">Document File *</label>
            {file ? (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <FileText size={14} className="text-blue-500" />
                <span className="text-xs text-gray-700 flex-1 truncate">{file.name}</span>
                <button onClick={() => setFile(null)} className="text-gray-400 hover:text-red-400"><X size={12} /></button>
              </div>
            ) : (
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg py-4 cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
                <Upload size={16} className="text-gray-400" />
                <span className="text-xs text-gray-500">Choose PDF, DOCX, XLSX, or TXT file</span>
                <input type="file" accept=".pdf,.docx,.doc,.txt,.text,.md,.xlsx,.xlsm" className="hidden" onChange={e => setFile(e.target.files?.[0])} data-testid="upload-file-input" />
              </label>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-gray-500 block mb-1">Carrier Name *</label>
              <Input value={carrier} onChange={e => setCarrier(e.target.value)} placeholder="e.g., Progressive" className="h-8 text-xs" data-testid="upload-carrier" />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-500 block mb-1">State</label>
              <select value={state} onChange={e => setState(e.target.value)} className="w-full h-8 text-xs border rounded-md px-2 bg-white" data-testid="upload-state">
                <option value="">All States</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-gray-500 block mb-1">Line of Business</label>
              <select value={lob} onChange={e => setLob(e.target.value)} className="w-full h-8 text-xs border rounded-md px-2 bg-white" data-testid="upload-lob">
                <option value="">Select LOB</option>
                {LOBS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-gray-500 block mb-1">Assigned Owner</label>
              <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)} className="w-full h-8 text-xs border rounded-md px-2 bg-white">
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          </div>

          {isAdmin && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold text-gray-500 block mb-1">Review Frequency (days)</label>
                <select value={reminderFreq} onChange={e => setReminderFreq(e.target.value)} className="w-full h-8 text-xs border rounded-md px-2 bg-white">
                  <option value="30">Every 30 days</option>
                  <option value="60">Every 60 days</option>
                  <option value="90">Every 90 days</option>
                  <option value="180">Every 6 months</option>
                  <option value="365">Every 12 months</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold text-gray-500 block mb-1">Escalate after # reminders</label>
                <Input type="number" value={escalationAfter} onChange={e => setEscalationAfter(e.target.value)} className="h-8 text-xs" min="1" max="20" />
              </div>
            </div>
          )}

          <div>
            <label className="text-[10px] font-semibold text-gray-500 block mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Version notes, changes from previous..." className="w-full text-xs p-2 border rounded-lg h-16 resize-none" />
          </div>

          <div className="flex gap-2 pt-2">
            <Button onClick={handleUpload} disabled={uploading} className="flex-1 h-9 text-xs bg-[#101C47]" data-testid="upload-submit">
              {uploading ? 'Uploading & Indexing...' : 'Upload & Index Document'}
            </Button>
            <Button variant="outline" onClick={onClose} className="h-9 text-xs">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
};
